import { Avatar, Box, Button, Checkbox, Flex, Group, Popover, Stack, Table as MantineTable, Text, TextInput } from "@mantine/core"
import { BroomIcon, ColumnsIcon, DownloadSimpleIcon, EyeIcon, LockIcon, MagnifyingGlassIcon, PlusIcon, UserIcon } from "@phosphor-icons/react"
import { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { NetworkApi } from "../../apis"
import { CustomsTable, ITableRef } from "../../components/custom-table"
import { PageLayout } from "../../components/layout"
import { usePermission } from "../../components/permission"
import { DotTag } from "../../components/purchase-sales/status-tag"
import { ListColumn, ListHead, nextSort, RowActionIcons } from "../../components/purchase-sales/list-table"
import { Sort } from "../../components/table"
import { ReasonTooltip } from "../../components/ui"
import RenderText from "../../components/ui/render-text"
import { INetworkPlus } from "../../models/Network"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"
import { renderName } from "../../utils/render-name"
import { CustomerFilters, INITIAL_CUSTOMER_FILTERS } from "./customer-filters"
import classes from "./list.module.css"

const person = (u?: { avatar?: string | null } | null, sub?: string | null) => (
  <Group gap="sm" wrap="nowrap">
    <Avatar src={u?.avatar || null} size="sm" radius="xl"><UserIcon size={14} /></Avatar>
    <Box>
      <RenderText text={renderName(u as never)} />
      {sub !== undefined && <RenderText text={sub} />}
    </Box>
  </Group>
)

export function SupplierCustomerList() {
  const { t } = useTranslation(["supplierSales", "common"])
  const navigate = useNavigate()
  const tableRef = useRef<ITableRef>(null)
  const [excelBusy, setExcelBusy] = useState(false)
  const [sorted, setSorted] = useState<[string, Sort]>(["createdAt", Sort.DESC]) // newest first; name sort needs order-service with the coBusiness ORDER fix
  const [draft, setDraft] = useState<CustomerFilters>(INITIAL_CUSTOMER_FILTERS)
  const [filters, setFilters] = useState<CustomerFilters>(INITIAL_CUSTOMER_FILTERS)
  const [extraColumns, setExtraColumns] = useState<string[]>([])

  const ORD_NET_LIST = usePermission("ORD_NET_LIST")
  const ORD_NET_DETAIL = usePermission("ORD_NET_DETAIL")
  const ORD_NET_NEW_ORDER = usePermission("ORD_NET_NEW_ORDER")
  const ORD_SAVE = usePermission("ORD_SAVE")
  const ORD_SEND = usePermission("ORD_SEND")
  // The order form saves or sends (BE order/create.ts), so a missing ORD_SAVE / ORD_SEND is a reason too.
  const newOrderReason = ORD_NET_NEW_ORDER.reason ?? (ORD_SAVE.hasAccess || ORD_SEND.hasAccess ? null : t("common:permission.required", { code: "ORD_SAVE / ORD_SEND" }))
  // BE network/get.ts accepts any of ORD_NET_DETAIL / ORD_NET_LIST: whoever sees this list may open a row.
  const detailReason = ORD_NET_DETAIL.hasAccess || ORD_NET_LIST.hasAccess ? null : ORD_NET_DETAIL.reason

  const c = (key: string) => t(`supplierSales:customer.${key}`)
  const allColumns = useMemo<ListColumn<INetworkPlus>[]>(
    () => [
      { id: "index", label: "#", render: (_r, index) => <RenderText text={index + 1} /> },
      {
        id: "partner", label: c("partnerName"), sort: "businessPartnerRefCodeBusinessName",
        render: (r) => (
          <Box>
            <Text fz={14} fw={600}>{r.partner?.businessName ?? "-"}</Text>
            <Text fz={14} c="gray.8" ff="monospace">{r.partner?.refCode ?? "-"}</Text>
          </Box>
        ),
      },
      { id: "regNumber", label: c("taxId"), sort: "regNumber", render: (r) => <Text fz={14} ff="monospace">{r.regNumber ?? "-"}</Text> },
      {
        id: "branch", label: c("receiverBranch"), sort: "receiverBrachRefCodeName",
        render: (r) => (
          <Box>
            <Text fz={14} fw={600}>{r.receiverBrach?.name ?? "-"}</Text>
            <Text fz={14} c="gray.8" ff="monospace">{r.receiverBrach?.refCode ?? "-"}</Text>
          </Box>
        ),
      },
      { id: "manager", label: c("manager"), sort: "orderStaffFirstName", render: (r) => person(r.staff) },
      { id: "classification", label: c("classification"), sort: "classificationCategoryId", render: (r) => <RenderText text={r.category?.name} /> },
      { id: "priority", label: c("priority"), sort: "classificationPriorityId", render: (r) => <RenderText text={r.priority?.name} /> },
      { id: "status", label: c("status"), sort: "isActive", render: (r) => <DotTag color={r.networkStatus === "ACTIVE" ? "green" : "gray"} label={r.networkStatus === "ACTIVE" ? c("active") : c("inactive")} /> },
      {
        id: "businessName", label: c("businessName"), optional: true, sort: "refCodeProfileName",
        render: (r) => (
          <Box>
            <Text fz={14} fw={600}>{r.profileName ?? "-"}</Text>
            <Text fz={14} c="gray.8" ff="monospace">{r.refCode ?? "-"}</Text>
          </Box>
        ),
      },
      { id: "finance", label: c("finance"), optional: true, sort: "financeStaffFirstNameEmail", render: (r) => person(r.receiverFinStaff, r.receiverFinStaff?.email ?? null) },
      { id: "region", label: c("region"), optional: true, sort: "areaRegionName", render: (r) => <RenderText text={r.region?.name} /> },
      { id: "direction", label: c("direction"), optional: true, sort: "areaDirectionName", render: (r) => <RenderText text={r.direction?.name} /> },
      { id: "responsible", label: c("responsibleManager"), optional: true, sort: "receiverStaffFirstNameEmail", render: (r) => person(r.receiverStaff, r.receiverStaff?.email ?? null) },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  )
  const columns = allColumns.filter((col) => !col.optional || extraColumns.includes(col.id))

  const apply = () => setFilters(draft)
  const clear = () => {
    setDraft(INITIAL_CUSTOMER_FILTERS)
    setFilters(INITIAL_CUSTOMER_FILTERS)
  }

  const excel = async () => {
    setExcelBusy(true)
    try {
      const res = await NetworkApi.excel({ filter: filters, order: sorted })
      if (!res?.url) {
        message.error(t("common:actions.exportFailed"))
        return
      }
      window.open(res.url as string, "_blank", "noopener,noreferrer")
    } catch (err) {
      message.error((err as HttpHandler)?.message!)
    } finally {
      setExcelBusy(false)
    }
  }

  return (
    <PageLayout
      title={t("supplierSales:customer.title")}
      subTitle={t("supplierSales:customer.subtitle")}
      breadcrumb={[{ to: "/", label: t("common:breadcrumb.home") }, { label: t("supplierSales:customer.breadcrumb") }]}
      extra={[
        <ReasonTooltip key="excel" reason={ORD_NET_LIST.reason}>
          <Button variant="default" leftSection={<DownloadSimpleIcon size={16} />} loading={excelBusy} onClick={excel}>
            {t("common:actions.exportExcel")}
          </Button>
        </ReasonTooltip>,
      ]}>
      {!ORD_NET_LIST.hasAccess ? (
        <Box>{ORD_NET_LIST.accessDenied()}</Box>
      ) : (
        <Flex direction="column" gap="md">
          <Group justify="space-between" align="flex-end" gap="md" wrap="wrap">
            <Group gap="sm" align="flex-end" wrap="wrap">
              <TextInput
                label={c("partnerNameLabel")}
                placeholder={c("searchByNamePlaceholder")}
                value={draft.businessPartnerRefCodeBusinessName}
                onChange={(e) => setDraft({ ...draft, businessPartnerRefCodeBusinessName: e.currentTarget.value })}
                onKeyDown={(e) => e.key === "Enter" && apply()}
                w={260}
              />
              <TextInput
                label={c("taxId")}
                placeholder={c("searchByTaxPlaceholder")}
                value={draft.regNumber}
                onChange={(e) => setDraft({ ...draft, regNumber: e.currentTarget.value })}
                onKeyDown={(e) => e.key === "Enter" && apply()}
                w={200}
              />
              <Button variant="default" leftSection={<BroomIcon size={16} />} onClick={clear}>{t("common:actions.clear")}</Button>
              <Button color="dark" leftSection={<MagnifyingGlassIcon size={16} />} onClick={apply}>{t("common:actions.search")}</Button>
            </Group>
            <Popover position="bottom-end" withArrow shadow="md" width={260}>
              <Popover.Target>
                <Button variant="default" leftSection={<ColumnsIcon size={16} />}>{t("supplierSales:sales.list.columnsMenu")}</Button>
              </Popover.Target>
              <Popover.Dropdown>
                <Stack gap="xs">
                  <Text fw={650} fz={14}>{t("supplierSales:sales.list.columnsMenuTitle")}</Text>
                  <Checkbox.Group value={extraColumns} onChange={setExtraColumns}>
                    <Stack gap="xs">
                      {allColumns.filter((col) => col.optional).map((col) => (
                        <Checkbox key={col.id} value={col.id} label={col.label} />
                      ))}
                    </Stack>
                  </Checkbox.Group>
                </Stack>
              </Popover.Dropdown>
            </Popover>
          </Group>

          <CustomsTable
            ref={tableRef}
            name="supplier-contract-customer.list"
            sorted={sorted}
            filters={filters as never}
            emptyTitle={c("emptyTitle")}
            onClearFilters={clear}
            loadData={(data) => NetworkApi.list(data!)}
            thead={<ListHead columns={columns} actionLabel={t("common:table.action")} sorted={sorted} onSort={(k) => setSorted((s) => nextSort(s, k))} />}
            tbody={(rows: INetworkPlus[]) => (
              <MantineTable.Tbody>
                {rows.map((record, index) => (
                  <MantineTable.Tr key={record.id ?? index}>
                    <MantineTable.Td>
                      <RowActionIcons
                        items={[
                          { key: "detail", label: c("detailTooltip"), icon: <EyeIcon size={18} />, reason: detailReason, onClick: () => navigate(`/supplier-customer/detail/${record.id}`) },
                          { key: "order", label: c("newOrderTooltip"), icon: <PlusIcon size={18} />, reason: newOrderReason, onClick: () => navigate(`/supplier-customer/order/${record.id}`) },
                        ]}
                      />
                    </MantineTable.Td>
                    {columns.map((col) => (
                      <MantineTable.Td key={col.id}>{col.render(record, index)}</MantineTable.Td>
                    ))}
                  </MantineTable.Tr>
                ))}
              </MantineTable.Tbody>
            )}
          />

          {!ORD_NET_NEW_ORDER.hasAccess && (
            <Box className={classes.footnote}>
              <LockIcon size={16} />
              <Text fz={14} c="gray.8">{t("supplierSales:customer.newOrderNote")}</Text>
            </Box>
          )}
        </Flex>
      )}
    </PageLayout>
  )
}
